

























































































































import { mapGetters } from 'vuex';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { truncateString } from '@/helpers/formatString';
import { DataTableHeader } from 'vuetify';
import {
  EJackpotCampaignSearchBy,
  EJackpotCampaignStatusType,
  IJackpotCampaign,
  IJackpotCampaignFiltersParams,
  IJackpotCampaignListParams,
  ValidationRules
} from '@/api/schema';
import mapValues from 'lodash/mapValues';
import * as vr from '@/helpers/validation';
import { getCampaignsList } from '@/api/CopyStake/GamificationCenter/Jackpot';

const CopyStakeJackpotCampaignsTableFiltersDialog = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeGamificationCenter/Jackpot/CampaignsTable/CopyStakeJackpotCampaignsTableFiltersDialog.vue'
  );
const CopyAddress = (): Promise<any> =>
  import('@/components/CopyAddress.vue');
const VStatus = (): Promise<any> => import('@/components/VStatus.vue');

type ISelectItem = {
  text: string;
  value: EJackpotCampaignSearchBy;
};

export const statusMap = {
  [EJackpotCampaignStatusType.ACTIVE]: {
    label: 'Running',
    type: 'system-success'
  },
  [EJackpotCampaignStatusType.DELETED]: {
    label: 'Deleted',
    type: 'additional-deleted'
  },
  [EJackpotCampaignStatusType.FINISHED]: {
    label: 'Finished',
    type: 'additional-finished'
  },
  [EJackpotCampaignStatusType.PENDING]: {
    label: 'Pending',
    type: 'additional-pending'
  }
};

const DEFAULT_PAGINATION_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['createdAt'],
  sortDesc: [false]
} as const;

const DEFAULT_SEARCH_OPTIONS = {
  searchBy: EJackpotCampaignSearchBy.NAME,
  search: ''
} as const;

export default {
  name: 'CopyStakeGamificationCenterJackpotCampaignsTable',
  components: {
    CopyStakeJackpotCampaignsTableFiltersDialog,
    CopyAddress,
    VStatus
  },
  props: {
    needToRefetch: {
      type: Boolean,
      default: false
    }
  },
  data(): Record<string, unknown> {
    return {
      EJackpotCampaignStatusType,
      statusMap,
      isJackpotCampaignsLoading: false,
      jackpotCampaigns: [],
      totalJackpotCampaigns: null,
      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS },
      tmpSearchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      searchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      filtersOptions: null,
      dialogs: {
        campaignsFilters: false
      }
    };
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    searchByOptions(): ISelectItem[] {
      return [
        {
          text: 'Name',
          value: EJackpotCampaignSearchBy.NAME
        },
        {
          text: 'Campaign ID',
          value: EJackpotCampaignSearchBy.JACKPOT_ID
        }
      ];
    },
    currentSearchBy(): ISelectItem | null {
      return (
        this.searchByOptions.find(
          (option: ISelectItem) =>
            option.value === this.tmpSearchOptions.searchBy
        ) ?? null
      );
    },
    searchPlaceholder(): string {
      if (!this.currentSearchBy) {
        return '';
      }

      return `Search by ${this.currentSearchBy.text}`;
    },
    headers(): DataTableHeader[] {
      return [
        { text: 'ID', value: 'id' },
        { text: 'Campaign Name', value: 'name' },
        {
          text: 'Operator User ID',
          value: 'streamer.streamerOperatorUserId'
        },
        { text: 'JP Target, USDT', value: 'jackpotExpected' },
        { text: 'JP Actual, USDT', value: 'jackpotCurrent' },
        { text: 'Creation Date', value: 'createdAt' },
        { text: 'Start (UTC)', value: 'startAt' },
        { text: 'End (UTC)', value: 'finishAt' },
        { text: 'Status', value: 'status', width: '150' },
        { text: 'Action', value: 'actions', sortable: false }
      ];
    },
    modifiedFilters(): IJackpotCampaignFiltersParams {
      const timeMap = {
        createdAfter: '00:00',
        startAfter: '00:00',
        createdBefore: '23:59',
        finishBefore: '23:59'
      };

      return mapValues(this.filtersOptions ?? {}, (value, key) =>
        key in timeMap && typeof value === 'string'
          ? `${value} ${timeMap[key]}`
          : value
      );
    },
    requestParams(): IJackpotCampaignListParams {
      return {
        ...(this.searchOptions.search
          ? { [this.searchOptions.searchBy]: this.searchOptions.search }
          : {}),
        ...this.modifiedFilters,
        size: this.tableOptions.itemsPerPage,
        page: this.tableOptions.page - 1,
        sort: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
        operatorId: this.operatorId
      };
    },
    rules(): ValidationRules {
      return {
        [EJackpotCampaignSearchBy.NAME]: [
          (v) => (v ? vr.minLength(v, 3) : true)
        ],
        [EJackpotCampaignSearchBy.JACKPOT_ID]: [
          (v) => (v ? vr.validNumber(v) : true),
          (v) => (v ? vr.positiveNumber(v) : true)
        ]
      };
    },
    getSearchRules(): ValidationRules {
      return this.rules[this.currentSearchBy.value];
    }
  },
  watch: {
    needToRefetch: {
      async handler(value: boolean): Promise<void> {
        if (value) {
          await this.fetchJackpotCampaigns();
        }
      }
    },
    requestParams: {
      async handler(): Promise<void> {
        await this.fetchJackpotCampaigns();
      },
      deep: true
    }
  },
  methods: {
    truncateString,
    async fetchJackpotCampaigns(): Promise<void> {
      try {
        this.isJackpotCampaignsLoading = true;

        const {
          content: jackpotCampaigns,
          totalElements: totalJackpotCampaigns
        } = await getCampaignsList(this.requestParams);

        this.jackpotCampaigns = jackpotCampaigns;
        this.totalJackpotCampaigns = totalJackpotCampaigns;

        this.$emit('update:needToRefetch', false);
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.isJackpotCampaignsLoading = false;
      }
    },

    updateRequestOptions(newOptions: Record<string, unknown>): void {
      const searchInput = this.$refs.searchInput;
      searchInput.validate();

      this.$nextTick((): void => {
        if (searchInput.valid) {
          this.searchOptions.searchBy =
            newOptions.searchBy ?? DEFAULT_SEARCH_OPTIONS.searchBy;
          this.searchOptions.search =
            newOptions.search ?? DEFAULT_SEARCH_OPTIONS.search;

          this.resetTableOptions();
        }
      });
    },

    resetTableOptions(): void {
      this.tableOptions.page = DEFAULT_PAGINATION_OPTIONS.page;
      this.tableOptions.itemsPerPage =
        DEFAULT_PAGINATION_OPTIONS.itemsPerPage;
    },

    handleClickClearSearchInput(): void {
      this.updateRequestOptions({
        ...this.tmpSearchOptions,
        search: ''
      });
    },
    handleKeydownEnterSearchInput(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    handleClickSearchButton(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    handleClickFiltersButton(): void {
      this.openCampaignTableFiltersDialog();
    },
    openCampaignTableFiltersDialog(): void {
      this.dialogs.campaignsFilters = true;
    },
    handleClickEditCampaignButton(campaign: IJackpotCampaign): void {
      this.$emit('edit-campaign', campaign);
    },
    handleClickActivateCampaignStatus(campaign: IJackpotCampaign): void {
      this.$emit('activate-campaign', campaign);
    },
    handleClickDeactivateCampaignStatus(campaign: IJackpotCampaign): void {
      this.$emit('deactivate-campaign', campaign);
    },
    handleClickCopyCampaignBtn(campaign: IJackpotCampaign): void {
      this.$emit('copy-campaign', campaign);
    }
  }
};
